import { useEffect, useState } from 'react';
import { Card } from '@ftedn/ui';
import { useApi } from '../../hooks/useApi';

interface SmsStats {
  todaySends: number;
  hardCap: number;
  percentUsed: number;
  circuitBreaker: boolean;
  recentFailures: Array<{ sid: string; error: string; at: string }>;
  recentInbound: Array<{ from: string; body: string; at: string }>;
}

export function SmsPage() {
  const { get, post } = useApi();
  const [stats, setStats] = useState<SmsStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    get('/twilio/stats')
      .then(setStats)
      .catch((e: unknown) => setError(String(e)));
  }, []);

  const toggleBreaker = async () => {
    if (!stats) return;
    setToggling(true);
    try {
      await post('/twilio/circuit-breaker', {
        enabled: !stats.circuitBreaker,
      });
      setStats({ ...stats, circuitBreaker: !stats.circuitBreaker });
    } catch (e) {
      setError(String(e));
    }
    setToggling(false);
  };

  if (error) return <Card><p style={{ color: 'var(--color-error)' }}>Error: {error}</p></Card>;
  if (!stats) return <Card><p>Loading SMS stats…</p></Card>;

  const capColor = stats.percentUsed >= 0.8
    ? stats.percentUsed >= 1 ? 'var(--color-error)' : 'var(--color-warning, #f59e0b)'
    : 'var(--color-success, #10b981)';

  return (
    <div>
      <h1>📱 SMS Management</h1>

      {/* Volume & Cap */}
      <Card>
        <h2>Today's Volume</h2>
        <div style={{ display: 'flex', gap: '2rem', marginTop: '1rem' }}>
          <div>
            <p style={{ fontSize: '2rem', fontWeight: 700, color: capColor }}>
              {stats.todaySends.toLocaleString()}
            </p>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              of {stats.hardCap.toLocaleString()} cap
            </p>
          </div>
          <div>
            <p style={{ fontSize: '2rem', fontWeight: 700 }}>
              {Math.round(stats.percentUsed * 100)}%
            </p>
            <p style={{ color: 'var(--color-text-secondary)' }}>used</p>
          </div>
        </div>
        {/* Progress bar */}
        <div style={{
          height: 8, borderRadius: 4, background: 'var(--color-surface-2, #e5e7eb)',
          marginTop: '1rem', overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', width: `${Math.min(stats.percentUsed * 100, 100)}%`,
            background: capColor, borderRadius: 4, transition: 'width 0.3s',
          }} />
        </div>
      </Card>

      {/* Circuit Breaker */}
      <Card>
        <h2>Circuit Breaker</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
          <span style={{
            display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
            background: stats.circuitBreaker ? 'var(--color-success, #10b981)' : 'var(--color-error, #ef4444)',
          }} />
          <span>{stats.circuitBreaker ? 'ENABLED (sends active)' : 'DISABLED (sends halted)'}</span>
          <button
            onClick={toggleBreaker}
            disabled={toggling}
            style={{
              padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
              background: stats.circuitBreaker ? 'var(--color-error, #ef4444)' : 'var(--color-success, #10b981)',
              color: '#fff', fontWeight: 600,
            }}
          >
            {toggling ? '…' : stats.circuitBreaker ? 'Disable SMS' : 'Enable SMS'}
          </button>
        </div>
      </Card>

      {/* Recent Failures */}
      <Card>
        <h2>Recent Failures</h2>
        {stats.recentFailures.length === 0 ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>No recent failures ✓</p>
        ) : (
          <ul>
            {stats.recentFailures.map((f, i) => (
              <li key={i}>
                <code>{f.sid}</code> — {f.error} ({f.at})
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Recent Inbound */}
      <Card>
        <h2>Recent Inbound Messages</h2>
        {stats.recentInbound.length === 0 ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>No inbound messages yet</p>
        ) : (
          <ul>
            {stats.recentInbound.map((m, i) => (
              <li key={i}>
                <strong>{m.from}</strong>: "{m.body}" ({m.at})
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
